import { useQuery } from '@sequencehq/api/utils'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  Button,
  InputSelectControlField,
  Modal,
  TextAreaField,
  TextInputField,
  Flex,
  useModalClose,
  useNotifications
} from '@sequencehq/core-components'
import Loading from 'components/Loading'
import { useState } from 'react'
import { TaxCategoryField } from 'common/drawers/PricingEditor/view/layout/listPrice/ProductForm/TaxCategoryField'
import { RevenueRecognitionMethod } from 'common/types/revenueRecognition.types'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useEditProduct } from 'modules/Products/drawers/EditProduct/useEditProduct'
import { XeroMappingFields } from 'Products/components/XeroMappingFields'
import { PRODUCT_DESCRIPTION_MAX_LENGTH } from 'Products/constants'
import { Product } from 'Products/types'

export const EditProductForm = (props: {
  productId: string
  onClose: () => void
}) => {
  const productQuery = useQuery(dashboardv99990101Client.getProduct, {
    id: props.productId
  })

  return (
    <Modal
      isOpen
      variant="drawer"
      width="550px"
      onClose={props.onClose}
      data-testid="editProduct.drawer"
    >
      <Modal.Header showCloseButton withBorder>
        Edit product
      </Modal.Header>
      {productQuery.data ? (
        <EditProductFormBody product={productQuery.data} />
      ) : (
        <Modal.Content>
          <Loading />
        </Modal.Content>
      )}
    </Modal>
  )
}

const EditProductFormBody = (props: { product: Product }) => {
  const {
    changeset,
    showXeroMappings,
    isXeroMappingsLoading,
    hasXeroMappings,
    fieldsConfig,
    functions: { editProduct }
  } = useEditProduct(props.product)
  const flags = useFeatureFlags()
  const requestClose = useModalClose()
  const { displayNotification } = useNotifications()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleUpdate = async () => {
    const submission = editProduct()

    // The form is invalid — validation errors are now shown, nothing submitted.
    if (!submission) {
      return
    }

    setIsSubmitting(true)

    try {
      // putProduct invalidates the getProduct cache, so the product page and
      // any other useProduct subscribers refetch the updated data.
      await submission
      displayNotification('Product updated', { type: 'success' })
      requestClose?.()
    } catch {
      // Keep the drawer open so the user's input is preserved for a retry.
      displayNotification('Failed to update product', { type: 'error' })
      setIsSubmitting(false)
    }
  }

  return (
    <>
      <Modal.Content padding="24px">
        <Flex direction="column" data-testid="editProduct" gap="16px">
          <TextInputField
            data-testid="editProduct.name"
            label="Product name"
            value={fieldsConfig.name.value}
            onChange={e => fieldsConfig.name.onChange(e.target.value)}
            validationErrors={fieldsConfig.name.validationErrors}
            isDisabled={fieldsConfig.name.disabled}
            autoFocus
          />
          <TextAreaField
            label="Description"
            value={fieldsConfig.label.value}
            onChange={fieldsConfig.label.onChange}
            validationErrors={fieldsConfig.label.validationErrors}
            isDisabled={fieldsConfig.label.disabled}
            maxLength={PRODUCT_DESCRIPTION_MAX_LENGTH}
          />

          <TaxCategoryField
            data-testid="editProduct.taxCategoryId"
            field={fieldsConfig.taxCategoryId}
            formChangeset={changeset}
          />

          {flags.useRevRec && (
            <InputSelectControlField
              label="Revenue recognition method"
              data-testid="editProduct.revenueRecognitionMethod"
              placeholder="Select method"
              {...fieldsConfig.revenueRecognitionMethod}
              initialValue={fieldsConfig.revenueRecognitionMethod.value}
              onChange={(value: string) => {
                fieldsConfig.revenueRecognitionMethod.onChange(
                  value as RevenueRecognitionMethod
                )
              }}
            />
          )}

          {showXeroMappings && !isXeroMappingsLoading && (
            <XeroMappingFields
              deferredField={fieldsConfig.deferredRevenueXeroAccountMappingId}
              recognizedField={
                fieldsConfig.recognizedRevenueXeroAccountMappingId
              }
              unbilledField={fieldsConfig.unbilledRevenueXeroAccountMappingId}
              hasMappings={hasXeroMappings}
              testIdPrefix="editProduct"
            />
          )}
        </Flex>
      </Modal.Content>

      <Modal.Footer withBorder>
        <Modal.CancelButton />
        <Button
          data-testid="editProduct.save"
          variant="primary"
          onClick={handleUpdate}
          submitting={isSubmitting}
          submittingText="Updating..."
        >
          Update
        </Button>
      </Modal.Footer>
    </>
  )
}
